import { Options } from "../../common/types/requestOptions";
import { api } from "../../app/api";
import { nanoid } from "@reduxjs/toolkit";
import { TempPasswordUpdated } from "../../common/components/functional/tempPasswordDialog/TempPasswordDialog";
import {
  BulkSalaryIncreaseQuery,
  MassUpdateClarityPayload,
  MassUpdateStatus,
  MassUpdateStatusQuery,
} from "./types/queries";

// import salariesData from "../../mocks/generated_salaries_1000.json";

export interface ExpenseType {
  id?: string;
  name: string;
  nameEN: string;
  nameSV: string;
  accountDebit: string | null;
  accountCredit: string | null;
  incomeTypeCodes: string[];
  pricingCategory: string | null;
  withHoldingTax: Boolean;
  pensionInsurance: Boolean;
  accidentInsurance: Boolean;
  unemploymentInsurance: Boolean;
  healthInsurance: Boolean;
}

export interface ExpenseTypeMapping {
  validFrom: string;
  expenseTypes: ExpenseType[];
}

export interface ExpenseTypeMappingPost {
  [index: number]: {
    payload: {
      validFrom: string;
      mapping: Record<string, ExpenseType>[];
    };
  };
}

export interface AccountsPost {
  [index: number]: {
    version: number;
    payload: Accounts;
  };
}

export interface Accounts {
  version: number;
  accounts: Record<string, string>[];
}

export interface Account {
  id: string;
  accountCode: string;
  name: string;
}

export interface SalesAccounts {
  version: number;
  validFrom: string;
  accounts: Record<string, {}>[];
}

export interface SalesAccountsPost {
  [index: number]: {
    version: number;
    validFrom: string;
    accounts: Record<string, {}>[];
  };
}

export interface SalesAccount {
  id: string;
  code: string;
  description: string;
  defaultVatPercent: string;
  defaultVatCode: string;
  canUseInContract: boolean;
  canUseInExternalSales: boolean;
  canUseInGroupInternalSales: boolean;
}

export interface IncomeTypesPost {
  [index: number]: {
    version: number;
    payload: IncomeTypes;
  };
}

export interface IncomeTypes {
  version: number;
  incomeTypes: Record<string, string>[];
}

export interface IncomeType {
  id: string;
  incomeCode: string;
  name: string;
}

export interface IncomeTypeFilters {}

export interface SalesAccountConfigFilters {
  validFrom: string;
}

export interface SchedulingPeriods {
  classId: string;
  type: string;
  version: number;
  periods: SchedulingPeriod[];
}

export interface SchedulingPeriod {
  id?: string;
  start: string;
  end: string;
  entriesApprovedByCustomer: number;
  entriesApprovedBySystem: number;
  invoicesSentWave1: number;
  invoicesSentWave2: number;
  payslipsLocked?: number;
  salaryDate: string; // palkanmaksupäivä
  valueDate: string; // arvopäivä (lasku ja palkka)
}

export interface SchedulingPeriodPost {
  classId: string;
  version: number;
  period: SchedulingPeriod;
}

export interface SchedulingPeriodEvent {
  id: string;
  version: number;
  name: string;
  payload: {
    schedulingPeriod: SchedulingPeriod;
  };
}

export interface SchedulingClassAndDatePayload {
  schedulingClassId: string;
  date: string;
}

export interface SendInvoicesPayload extends SchedulingClassAndDatePayload {
  wave: string;
}

export interface RedactTalentDataPayload {
  talentId: string;
}

export interface AutoApproveEntriesPayload {
  periodClass: string;
  range: {
    start: string;
    end: string;
  };
}

export interface RebuildWorkHistoryPayload {
  talentId: string;
  startDate: string;
  endDate: string;
}

export interface RecalculateCustomerPricingPayload {
  contractId: string;
  periodDate: string;
}

export interface CreateSalaryPaymentPayload {
  eezyCompanyId: string;
  schedulingClassId: string;
  date: string;
  numberOfPayslips: string;
}

export interface CreateTaxPaymentPayload {
  eezyCompanyId: string;
  date: string;
}

export interface ExportTaxPaymentPayload {
  eezyCompanyId: string;
  date: string;
}

export interface CreateGarnishmentPaymentPayload {
  eezyCompanyId: string;
  schedulingClassId: string;
  date: string;
}

export interface ExportGarnishmentPaymentPayload {
  eezyCompanyId: string;
  schedulingClassId: string;
  date: string;
}

export interface CreateTradeUnionPaymentPayload {
  eezyCompanyId: string;
  date: string;
}

export interface ExportTradeUnionPaymentPayload {
  eezyCompanyId: string;
  date: string;
}

export interface CreatePayerSummaryReportDeliveryPayload {
  eezyCompanyId: string;
  reportMonth: string;
}

export interface UpdatePayerSummaryReportDeliveryStatusPayload {
  payerSummaryReportDeliveryId: string;
}

export interface CreateWageReportDeliveryPayload {
  payslipId: string;
}

export interface UpdateWageReportDeliveryStatusPayload {
  wageReportDeliveryId: string;
}

export interface DeleteWageReportDeliveryPayload {
  wageReportDeliveryId: string;
}

export interface AcceptWorkAgreementPayload {
  id: String;
  version: Number;
}

export interface CreateHolidayBacklogReportsPayload {
  date: string;
}

export enum Service {
  Working = "working",
  Invoicing = "invoicing",
  Identity = "identity",
  Order = "order",
  Matching = "matching",
  Default = "",
  Recuitment = "recruitment",
}

export interface InspectEventsFilters {
  id: string;
  aggregateType: string;
}

export interface InspectEventsFormValues {
  id: string;
  aggregateType: string;
  service: Service;
}

export interface InspectEventsResponse {
  id: string;
  version: number;
  name: string;
  time: string;
  userId: string;
  requestId: string;
  payload: object;
}

export enum UnitType {
  FixedPrice = "FIXED_PRICE",
  SalaryMultiplier = "SALARY_MULTIPLIER",
}

export interface Price {
  unitValue: string;
  unit: UnitType;
  unitValueOverrideAllowed: boolean;
}

export interface TesPriceList {
  version: number | null;
  tesId: string;
  validFrom: string;
  prices: Record<string, Price>;
}

export interface UpdateTesPriceList {
  tesId: string | null;
  validFrom: string;
  version: number | null;
  prices: Record<string, Price>;
}

export interface UpdateTesPriceListEvent {
  id: string;
  version: number;
  name: string;
  payload: {
    tesId: string;
    validFrom: string;
    prices: Record<string, Price>;
  };
}

export interface CancelShift {
  assignmentId: string;
  paySalary: boolean;
  reason: string;
}

export interface SetTalentTemporaryPassword {
  talentId: string;
}

export interface UpdateInvoicePayload {
  invoiceId: string;
}

export interface DeleteInvoicePayload {
  invoiceId: string;
}

export interface UpdateTalentStatusPayload {
  talentId: string;
  status: string;
  version: number;
}

export interface UpdateTalentEmailAddressPayload {
  talentId: string;
  emailAddress: string;
}

export interface RedactionPlanStage {
  type: string;
  startAfterDate?: string;
  startedAt?: number;
  updatedAt?: number;
  endedAt?: number;
}

export interface RedactionPlan {
  id: string;
  type: string;
  isActive: boolean;
  stages: RedactionPlanStage[];
}

export const getIncomeTypes = ({ requestId }: Options = {}) => {
  return api.get<IncomeTypes>("/projections/qry/incomeTypes", {
    requestId,
    auth: true,
  });
};

export const saveIncomeTypes = (
  incomeTypes: IncomeTypes,
  { requestId }: Options,
) => {
  const { version, incomeTypes: newIncomeTypes } = incomeTypes;
  return api.post<IncomeTypesPost>("/invoicing/cmd/changeIncomeTypesConfig", {
    requestId,
    auth: true,
    body: {
      version,
      incomeTypes: newIncomeTypes,
    },
  });
};

export const getAccounts = ({ requestId }: Options = {}) => {
  return api.get<Accounts>("/projections/qry/accounts", {
    requestId,
    auth: true,
  });
};

export const getRedactionPlanByTalentId = (talentId: string) => {
  return api.get<RedactionPlan>("/projections/qry/redactionPlanByTalentId", {
    requestId: nanoid(),
    auth: true,
    queryParams: {
      talentId,
    },
  });
};

export const saveAccounts = (accounts: Accounts, { requestId }: Options) => {
  const { version, accounts: newAccounts } = accounts;
  return api.post<AccountsPost>("/invoicing/cmd/changeAccountsConfig", {
    requestId,
    auth: true,
    body: {
      version,
      accounts: newAccounts,
    },
  });
};

export const getSalesAccountConfigDates = () => {
  return api.get<Promise<string[]>>(
    "/projections/qry/salesAccountConfigDates",
    {
      requestId: nanoid(),
      auth: true,
    },
  );
};

export const getSalesAccountConfigForDate = ({
  validFrom,
}: SalesAccountConfigFilters) => {
  return api.get<SalesAccounts>("/projections/qry/salesAccountConfigForDate", {
    requestId: nanoid(),
    auth: true,
    queryParams: {
      date: validFrom,
    },
  });
};

export const saveSalesAccounts = (
  accounts: SalesAccounts,
  { requestId }: Options,
) => {
  const { version, accounts: newAccounts, validFrom } = accounts;
  return api.post<SalesAccountsPost>(
    "/invoicing/cmd/changeSalesAccountsConfig",
    {
      requestId,
      auth: true,
      body: {
        version,
        accounts: newAccounts,
        validFrom,
      },
    },
  );
};

export const getExpenseTypeMappings = ({ requestId }: Options = {}) => {
  return api.get<string[]>("/projections/qry/expenseTypeMappings", {
    requestId,
    auth: true,
  });
};

export const getExpenseTypeMappingsForDate = (
  date: string,
  { requestId }: Options = {},
) => {
  return api.get<ExpenseTypeMapping>(
    "/projections/qry/expenseTypeMappingForDate?date=" + date,
    {
      requestId,
      auth: true,
    },
  );
};

export const addExpenseTypeMapping = (
  expenseTypeMappings: ExpenseTypeMapping,
  { requestId }: Options,
) => {
  const { validFrom, expenseTypes } = expenseTypeMappings;
  return api.post<ExpenseTypeMappingPost>(
    "/invoicing/cmd/addExpenseTypeMapping",
    {
      requestId,
      auth: true,
      body: {
        validFrom,
        expenseTypes,
      },
    },
  );
};

export const updateExpenseTypeMapping = (
  expenseTypeMappings: ExpenseTypeMapping,
  { requestId }: Options,
) => {
  const { validFrom, expenseTypes } = expenseTypeMappings;
  return api.post<ExpenseTypeMappingPost>(
    "/invoicing/cmd/changeExpenseTypeMapping",
    {
      requestId,
      auth: true,
      body: {
        validFrom,
        expenseTypes,
      },
    },
  );
};

export const getSchedulingPeriodsBySchedulingClassAndYear = (
  schedulingClassId: string,
  year: string,
  { requestId }: Options = {},
) => {
  return api.get<SchedulingPeriods>(
    `/projections/qry/schedulingPeriodsBySchedulingClassAndYear?schedulingClassId=${schedulingClassId}&year=${year}`,
    {
      requestId,
      auth: true,
    },
  );
};

export const addNewSchedulingPeriod = (
  schedulingPeriod: SchedulingPeriodPost,
  { requestId }: Options,
) => {
  const { classId, version, period } = schedulingPeriod;
  return api.post<SchedulingPeriodEvent[]>(
    "/invoicing/cmd/addSchedulingPeriod",
    {
      requestId,
      auth: true,
      body: {
        classId,
        version,
        period,
      },
    },
  );
};

export const updateOneSchedulingPeriod = (
  schedulingPeriod: SchedulingPeriodPost,
  { requestId }: Options,
) => {
  const { classId, version, period } = schedulingPeriod;
  return api.post<SchedulingPeriodEvent[]>(
    "/invoicing/cmd/updateSchedulingPeriod",
    {
      requestId,
      auth: true,
      body: {
        classId,
        version,
        period,
      },
    },
  );
};

export const runCreateInvoices = (payload: SchedulingClassAndDatePayload) => {
  return api.post("/invoicing/cmd/runCreateInvoices", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

export const runLockInvoices = (payload: SendInvoicesPayload) => {
  return api.post("/invoicing/cmd/runLockInvoices", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

export const runSendInvoices = (payload: SendInvoicesPayload) => {
  return api.post("/invoicing/cmd/runSendInvoices", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

export const runCreatePayslips = (payload: SchedulingClassAndDatePayload) => {
  return api.post("/invoicing/cmd/runCreatePayslips", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

export const runApprovePayslips = (payload: SchedulingClassAndDatePayload) => {
  return api.post("/invoicing/cmd/runApprovePayslips", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

export const runLockPayslips = (payload: SchedulingClassAndDatePayload) => {
  return api.post("/invoicing/cmd/runLockPayslips", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

export const runCreateHolidayBacklogReports = (
  payload: CreateHolidayBacklogReportsPayload,
) => {
  return api.post("/invoicing/cmd/runCreateHolidayBacklogReports", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

export const runRedactTalentData = (payload: RedactTalentDataPayload) => {
  return api.post("/identity/cmd/runRedactTalentData", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

export const runAutoApproveEntries = (payload: AutoApproveEntriesPayload) => {
  return api.post("/working/cmd/autoApproveEntries", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

export const runRebuildWorkHistoryTimes = (
  payload: RebuildWorkHistoryPayload,
) => {
  return api.post("/matching/cmd/rebuildWorkHistoryTimes", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

export const runRecalculateCustomerPricing = (
  payload: RecalculateCustomerPricingPayload,
) => {
  return api.post("/invoicing/cmd/recalculateCustomerPricing", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

export const runCreateSalaryPayment = (payload: CreateSalaryPaymentPayload) => {
  return api.post("/invoicing/cmd/createSalaryPayment", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

export const runCreateTaxPayment = (payload: CreateTaxPaymentPayload) => {
  return api.post("/invoicing/cmd/createTaxPayment", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

export const runExportTaxPayment = (payload: ExportTaxPaymentPayload) => {
  return api.post("/invoicing/cmd/exportTaxPayment", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

export const runCreateGarnishmentPayment = (
  payload: CreateGarnishmentPaymentPayload,
) => {
  return api.post("/invoicing/cmd/createGarnishmentPayment", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

export const runExportGarnishmentPayment = (
  payload: ExportGarnishmentPaymentPayload,
) => {
  return api.post("/invoicing/cmd/exportGarnishmentPayment", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

export const runCreateTradeUnionPayment = (
  payload: CreateTradeUnionPaymentPayload,
) => {
  return api.post("/invoicing/cmd/createTradeUnionPayment", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

export const runExportTradeUnionPayment = (
  payload: ExportTradeUnionPaymentPayload,
) => {
  return api.post("/invoicing/cmd/exportTradeUnionPayment", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

export const runCreatePayerSummaryReportDelivery = (
  payload: CreatePayerSummaryReportDeliveryPayload,
) => {
  return api.post("/invoicing/cmd/createPayerSummaryReportDelivery", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

export const runUpdatePayerSummaryReportDeliveryStatus = (
  payload: UpdatePayerSummaryReportDeliveryStatusPayload,
) => {
  return api.post("/invoicing/cmd/updatePayerSummaryReportDeliveryStatus", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

export const runCreateWageReportDelivery = (
  payload: CreateWageReportDeliveryPayload,
) => {
  return api.post("/invoicing/cmd/createWageReportDelivery", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

export const runUpdateWageReportDeliveryStatus = (
  payload: UpdateWageReportDeliveryStatusPayload,
) => {
  return api.post("/invoicing/cmd/updateWageReportDeliveryStatus", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

export const runDeleteWageReportDelivery = (
  payload: DeleteWageReportDeliveryPayload,
) => {
  return api.post("/invoicing/cmd/deleteWageReportDelivery", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

export const runAcceptWorkAgreement = (payload: AcceptWorkAgreementPayload) => {
  return api.post("/order/cmd/acceptWorkAgreement", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

// query events dynamically by service
export const inspectEvents = async (
  filters: InspectEventsFilters,
  service: Service,
  { requestId }: Options,
): Promise<InspectEventsResponse[]> => {
  return api
    .get<Promise<InspectEventsResponse[]>>(`/${service}/qry/inspectEvents`, {
      requestId,
      auth: true,
      queryParams: {
        ...filters,
      },
    })
    .then((res) => res.data);
};

export const getTesPriceList = async (payload: {
  tesId?: string;
  date: string;
}) => {
  let params: Record<string, string> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (value.toString()) {
      params[key] = value.toString();
    }
  }
  return api
    .get<TesPriceList[]>("/working/qry/tesPriceList", {
      requestId: nanoid(),
      auth: true,
      queryParams: {
        ...params,
      },
    })
    .then((res) => res.data);
};

export const runUpdateTesPriceList = (payload: UpdateTesPriceList) => {
  return api.post<UpdateTesPriceListEvent[]>(
    "/working/cmd/updateTesPriceList",
    {
      requestId: nanoid(),
      auth: true,
      body: payload,
    },
  );
};

export const runCancelShift = async ({
  assignmentId,
  paySalary,
  reason,
}: CancelShift) => {
  return api.post<any>(`/working/cmd/cancelShift`, {
    requestId: nanoid(),
    auth: true,
    body: {
      assignmentId,
      paySalary,
      reason,
    },
  });
};

export const runSetTalentTemporaryPassword = async ({
  talentId,
}: SetTalentTemporaryPassword) => {
  return api.post<TempPasswordUpdated>(
    `/identity/cmd/setTalentTemporaryPassword`,
    {
      requestId: nanoid(),
      auth: true,
      body: {
        talentId,
      },
    },
  );
};

// export const TARGET_OPTIONS: NameIdOption[] = [
//   { id: "employment", name: "Työsuhde" },
//   { id: "shift_salaries", name: "Vuorojen palkat" },
//   { id: "overrides", name: "Kiinnitys (overridet)" },
// ];

export const runUpdateInvoice = (payload: UpdateInvoicePayload) => {
  return api.post("/invoicing/cmd/updateInvoice", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

export const runUpdateTalentStatus = (payload: UpdateTalentStatusPayload) => {
  return api.post("/identity/cmd/updateTalentStatus", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

export const runUpdateTalentEmailAddress = (
  payload: UpdateTalentEmailAddressPayload,
) => {
  return api.post("/identity/cmd/updateTalentEmailAddress", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

// get mock salaries for bulk salary increase
// export const getSalaries = async (
//   filters: GetSalariesFilters,
//   { requestId }: Options = {},
// ) => {
//   console.log("getSalaries query payload: ", filters);
//   return new Promise<{ data: SalaryRows }>((resolve) => {
//     setTimeout(() => {
//       if (filters.target === "shift_salaries") {
//         resolve({ data: MOCK_SHIFT_SALARIES_API_RESPONSE });
//       } else if (filters.target === "employment") {
//         resolve({ data: MOCK_EMPLOYMENT_SALARIES_API_RESPONSE });
//       } else {
//         resolve({ data: [] });
//       }
//     }, 1000);
//   });
// };

export const bulkSalaryIncreaseQuery = async (
  filters: BulkSalaryIncreaseQuery,
  { requestId }: Options,
): Promise<any> => {
  // if (filters.mock) {
  //   return new Promise((resolve) => {
  //     setTimeout(() => {
  //       resolve(salariesData);
  //     }, 1000);
  //   });
  // }
  return api
    .get<Promise<any>>(`/projections/qry/bulkSalaryIncreaseQuery`, {
      requestId,
      auth: true,
      queryParams: {
        ...filters,
      },
    })
    .then((res) => {
      // console.log("bulkSalaryIncreaseQuery response: ", res.data);
      return res.data;
    });
};

interface MassUpdateResponse {
  massUpdate: MassUpdateStatus;
}

export const bulkSalaryIncreaseStateQuery = async (
  params: MassUpdateStatusQuery,
  { requestId }: Options,
): Promise<MassUpdateStatus> => {
  if (params.mock) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          massUpdateId: params.massUpdateId,
          unclearRows: ["employment123"],
        });
      }, 1000);
    });
  }
  return api
    .get<MassUpdateResponse>(`/projections/qry/massUpdateState`, {
      requestId,
      auth: true,
      queryParams: {
        ...params,
      },
    })
    .then((res) => {
      console.log("bulkSalaryIncreaseStateQuery response: ", res.data);
      return res.data.massUpdate;
    });
};

export const updateMassUpdateClarities = async (
  params: MassUpdateClarityPayload,
) => {
  return api.post<any>(`/working/cmd/setMassUpdateRowClarity`, {
    requestId: nanoid(),
    auth: true,
    body: {
      ...params,
    },
  });
};

export interface AcceptWorkOffer {
  bundleId: string;
  talentId: string;
  startingShiftAt?: string | null;
}

export interface ProcessWorkOffersPayload {
  workOffersId: string;
}

export const runAcceptWorkOffer = async ({
  bundleId,
  talentId,
  startingShiftAt,
}: AcceptWorkOffer) => {
  return api
    .post<any>(`/order/cmd/acceptWorkOfferForTalent`, {
      requestId: nanoid(),
      auth: true,
      body: {
        bundleId,
        talentId,
        startingShiftAt,
      },
    })
    .then((res) => res.data);
};

export const runProcessWorkOffers = async ({
  workOffersId,
}: ProcessWorkOffersPayload) => {
  return api
    .post<any>(`/order/cmd/processWorkOffers`, {
      requestId: nanoid(),
      auth: true,
      body: {
        workOffersId,
      },
    })
    .then((res) => res.data);
};

export const runDeleteInvoice = (payload: UpdateInvoicePayload) => {
  return api.post("/invoicing/cmd/deleteInvoice", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};
